import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { useDirectedVideoContext } from '../../DirectedVideoContext';
import { directedVideoPath } from '../../directedVideoPath';
import { howMadeMessages } from '../../messages/howMadeMessages';
import { resultMessages as messages } from '../../messages/resultMessages';
import { ResultClipStrip } from './ResultClipStrip';
import { ResultFacts } from './ResultFacts';
import { ResultPlayer } from './ResultPlayer';
import { directedAssetViewerPath, directedResultFacts } from './resultRules';
import { boldPart } from '../../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the result; each part uses its own slice of it */
import styles from './Result.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

/**
 * The finished directed video (design directed-video k1 to k3): the player of the rendered file and
 * the stills of the new clips on the left; the story and the style, the details, and the actions on
 * the right. "Download, share or post" opens the asset viewer of the render (the link of the "asset
 * is ready" email). "Make another video" opens the wizard with no video uuid: this video is ready,
 * so the opener makes a new one, and this video stays. "How it was made" (design l1) and the step
 * bar show the earlier steps.
 */
const ResultViewComponent: React.FC<{ cutUuid: string }> = ({ cutUuid }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = params.get('from');
  const { accountId, catalogItemUuid, video, song, readOnly, openEditor, leave, openMade } = useDirectedVideoContext();
  const facts = useMemo(() => directedResultFacts(video), [video]);
  const story = video.story?.title;
  const style = video.style?.title;
  const asset = video.asset ?? null;
  const viewerPath =
    asset !== null && asset.status === 'ready' && asset.fileUrl !== null
      ? directedAssetViewerPath(catalogItemUuid, accountId, asset.uuid)
      : null;

  return (
    <div className={styles.result} data-testid="directed-ready">
      <div className={styles.play}>
        <ResultPlayer />
        <ResultClipStrip clipsMade={facts.clipsMade} />
      </div>
      <div className={styles.side}>
        <span className={styles.chip}>
          <i aria-hidden />
          {intl.formatMessage(messages.chip)}
        </span>
        <h2>{intl.formatMessage(messages.title)}</h2>
        <p className={styles.story}>
          {story !== undefined && style !== undefined
            ? intl.formatMessage(messages.story, { b: bold, story, style, title: song.title })
            : intl.formatMessage(messages.storyPlain, { title: song.title })}
        </p>
        <ResultFacts facts={facts} />
        <div className={styles.actions}>
          <SMLButton variant="primary" onClick={() => openEditor(cutUuid)}>
            {intl.formatMessage(messages.openInEditor)}
          </SMLButton>
          {viewerPath !== null && (
            <SMLButton
              onClick={() => {
                // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
                void navigate(viewerPath);
              }}
            >
              {intl.formatMessage(messages.downloadShare)}
            </SMLButton>
          )}
          <SMLButton
            disabled={readOnly}
            onClick={() => {
              // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
              void navigate(directedVideoPath(catalogItemUuid, null, { accountId, from }));
            }}
          >
            {intl.formatMessage(messages.makeAnother)}
          </SMLButton>
          <SMLButton onClick={leave}>{intl.formatMessage(messages.backToSong)}</SMLButton>
        </div>
        <button
          type="button"
          className={styles.howLink}
          onClick={() => openMade(null)}
          data-testid="directed-made-link"
        >
          <span className={styles.howText}>
            <b>{intl.formatMessage(howMadeMessages.resultLink)}</b>
            <span>{intl.formatMessage(howMadeMessages.resultLinkText)}</span>
          </span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {facts.clipsMade > 0 && (
          <p className={styles.hint}>{intl.formatMessage(messages.footage, { count: facts.clipsMade })}</p>
        )}
      </div>
    </div>
  );
};

export const ResultView = TypedMemo(ResultViewComponent);
